import Sidebar from "../components/common/sidebar";
import useBranchGuard from "../hooks/useBranchGuard";
import { useParams } from "react-router-dom";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import MyTable from "../components/common/table";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllReports,
  getReportDetails,
  MAX_REPORT_ATTEMPTS,
} from "../redux/reports/reportsSlice";
import SidebarSkeleton from "../components/common/sidebarSkeleton";
import ErrorDisplay from "../components/common/error";
import TableSkeleton from "../components/common/tableSkeleton";
import {
  FormatCalendar,
  extractIp,
  formatDate,
  formatToDate,
  lastMonth,
  lastWeek,
  thisMonth,
  thisWeek,
  thisYear,
  today,
  yesterday,
} from "../redux/utils";
import CustomInput from "../components/common/customInput";
import { filterReportTable, isEmpty } from "../utils/filterReportTable";
import DateTimePicker from "../components/common/dateTimePicker";
import { useTranslation } from "react-i18next";
import { exportExcel, exportPdf } from "../utils/exporters";
import { formatName } from "../utils/reportName";
import { humanizeError } from "../utils/errorText";
import { PiFilePdf, PiFileXls } from "react-icons/pi";
import {
  getTicketDetail,
  resetGetTicketDetail,
} from "../redux/tickets/ticketSlice";
import TicketPopUp from "../components/reportsPage/reportPopup";
import CustomSelect from "../components/common/customSelect";

const PRESETS = {
  Today: today,
  Yesterday: yesterday,
  ThisWeek: thisWeek,
  LastWeek: lastWeek,
  ThisMonth: thisMonth,
  LastMonth: lastMonth,
  ThisYear: thisYear,
};

const rowKey = (row) =>
  row?.id ? row.id : row?.name ? row.name : row?.Name ? row.Name : row;

const outlineButton =
  "inline-flex items-center gap-1.5 rounded-md border border-solid border-[--gr-3] bg-[--btn-txt] px-3 py-2 text-sm font-medium text-[--gr-9] hover:bg-[--gr-0] disabled:opacity-50";

const ReportDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const topRef = useRef(null);
  const scrollInTable = useRef(null);
  const requestRef = useRef(null);

  const branchUrl = useParams().id;
  const reportFromUrl = useParams().reportId;
  const [selectedRow, setSelectedRow] = useState(reportFromUrl);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [open, setOpen] = useState(false);
  const [ticketData, setTicketData] = useState(null);
  const [customDate, setCustomDate] = useState(null);

  const { branches } = useSelector((state) => state.branches);
  const currentBranch =
    branches.length > 0
      ? branches.find((branch) => extractIp(branch.ipAddress) === branchUrl)
          ?.name
      : null;

  const translations = {
    endD: t("exel.endD"),
    header: t("exel.header"),
    name: t("exel.name"),
    startD: t("exel.startD"),
    tableName: t("exel.tableName"),
    branchName: t("exel.branchName"),
  };

  const {
    loading: reportsLoading,
    success: reportsSuccess,
    reports,
    error: reportsError,
  } = useSelector((state) => state.reports.allReports);
  const {
    loading: dataLoading,
    data,
    error: dataError,
    attempt,
    stale,
  } = useSelector((state) => state.reports.reportDetails);
  const {
    loading: ticketLoading,
    success: ticketSucc,
    ticket,
  } = useSelector((state) => state.tickets.getTicketDetail);

  useBranchGuard();

  /** Önceki rapor isteğini iptal edip yenisini başlatır (hızlı geçişlerde eski yanıt üstüne yazamaz). */
  const runReport = useCallback(
    (args) => {
      requestRef.current?.abort();
      requestRef.current = dispatch(getReportDetails(args));
    },
    [dispatch]
  );

  useEffect(() => {
    return () => requestRef.current?.abort();
  }, []);

  useEffect(() => {
    dispatch(getAllReports({ branchUrl }));
  }, [dispatch, branchUrl]);

  // Liste gelince seçili rapor yoksa ilkini seç
  useEffect(() => {
    if (!selectedRow && reportsSuccess && reports.length > 0) {
      setSelectedRow(rowKey(reports[0]));
    }
  }, [selectedRow, reportsSuccess, reports]);

  // Seçim değişince raporu getir (varsayılan dönemle)
  useEffect(() => {
    if (!selectedRow) return;
    setCustomDate(null);
    runReport({ branchUrl, reportId: selectedRow });
  }, [selectedRow, branchUrl, runReport]);

  useEffect(() => {
    if (data?.startDate && data?.endDate) {
      setStartDate(formatToDate(data.startDate));
      setEndDate(formatToDate(data.endDate));
    }
  }, [data]);

  const filteredTables = useMemo(
    () => (data ? filterReportTable(data, searchQuery) : null),
    [data, searchQuery]
  );
  const isTableEmpty = useMemo(
    () => (filteredTables ? isEmpty(filteredTables) : null),
    [filteredTables]
  );

  const submitDateRange = (start_date, end_date) => {
    if (!selectedRow) return;
    runReport({
      branchUrl,
      reportId: selectedRow,
      start_date: start_date || (startDate ? formatDate(startDate) : null),
      end_date: end_date || (endDate ? formatDate(endDate) : null),
    });
  };

  const handleOptionChange = (selectedOption) => {
    setCustomDate(selectedOption);
    const preset = PRESETS[selectedOption?.value];
    if (preset) {
      const range = preset();
      setStartDate(formatToDate(range.start));
      setEndDate(formatToDate(range.end));
      submitDateRange(range.start, range.end);
    } else if (selectedOption?.value === "Default") {
      runReport({ branchUrl, reportId: selectedRow });
    }
  };

  // Adisyon penceresi
  const showTickDetail = (ticket_id) => {
    dispatch(getTicketDetail({ branchUrl, ticket_id }));
    setOpen(true);
  };

  useEffect(() => {
    if (ticketSucc) {
      setTicketData(ticket);
      dispatch(resetGetTicketDetail());
    }
  }, [ticketSucc, dispatch, ticket]);

  useEffect(() => {
    if (!open) setTicketData(null);
  }, [open]);

  useEffect(() => {
    if (open) {
      document.body.classList.add("body_overflow_hidden");
    } else {
      document.body.classList.remove("body_overflow_hidden");
    }
    return () => {
      document.body.classList.remove("body_overflow_hidden");
    };
  }, [open]);

  // Rapor değişince en üste dön
  useEffect(() => {
    if (scrollInTable.current) {
      scrollInTable.current.scrollTop = 0;
    }
    if (topRef.current && window.innerWidth < 950) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [selectedRow]);

  const periodText = data
    ? `${FormatCalendar(data.startDate, ".", true, false)} – ${FormatCalendar(
        data.endDate,
        ".",
        true,
        false
      )}`
    : "";

  const retryText =
    dataLoading && attempt > 1
      ? t("reports.retrying", { attempt, max: MAX_REPORT_ATTEMPTS })
      : null;

  const presetOptions = [
    { value: "Default", label: t("options.customDate") },
    { value: "Today", label: t("options.today") },
    { value: "Yesterday", label: t("options.yesterday") },
    { value: "ThisWeek", label: t("options.thisWeek") },
    { value: "LastWeek", label: t("options.lastWeek") },
    { value: "ThisMonth", label: t("options.thisMonth") },
    { value: "LastMonth", label: t("options.lastMonth") },
    { value: "ThisYear", label: t("options.thisYear") },
  ];

  return (
    <section className="relative full_height-footer" ref={topRef}>
      {ticketData && (
        <TicketPopUp
          open={open}
          setOpen={setOpen}
          ticket={ticketData}
          loading={ticketLoading}
        />
      )}

      <div className="flex 950m:gap-3 full_height-footer">
        <aside>
          {reportsSuccess ? (
            <Sidebar
              title={`${reports.length} ${t("sidebar.reports")}`}
              data={reports}
              selectedRow={selectedRow}
              setSelectedRow={setSelectedRow}
            />
          ) : reportsError ? (
            <ErrorDisplay
              error={reportsError}
              onRetry={() => dispatch(getAllReports({ branchUrl, force: true }))}
            />
          ) : reportsLoading ? (
            <SidebarSkeleton />
          ) : null}
        </aside>

        <main className="flex-1 min-w-0 overflow-x-hidden pb-20 950m:pb-0">
          <div
            className="my-2 w-full 950m:max-h-[calc(100dvh-9rem)] 950m:overflow-y-auto"
            ref={scrollInTable}
          >
            <div className="w-full max-w-6xl mx-auto">
              {data ? (
                <>
                  {/* Başlık kartı */}
                  <div className="mb-3 rounded-xl bg-[--components-color] border border-solid border-[--gr-3] shadow-sm">
                    <div className="p-4 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                      <div className="min-w-0">
                        {currentBranch && (
                          <p className="text-xs font-medium uppercase tracking-wider text-[--tx-3] mb-0.5">
                            {currentBranch}
                          </p>
                        )}
                        <h1 className="text-xl font-semibold text-[--gr-9] truncate Poppins">
                          {formatName(data)}
                        </h1>
                        <p className="text-sm text-[--tx-3] mt-0.5">
                          {t("reports.period")}: {periodText}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          className={outlineButton}
                          onClick={() =>
                            exportPdf(data, translations, currentBranch)
                          }
                          aria-label={`${t("common.download")} PDF`}
                        >
                          <PiFilePdf className="text-lg" aria-hidden="true" />
                          PDF
                        </button>
                        <button
                          type="button"
                          className={outlineButton}
                          onClick={() =>
                            exportExcel(data, translations, currentBranch)
                          }
                          aria-label={`${t("common.download")} Excel`}
                        >
                          <PiFileXls className="text-lg" aria-hidden="true" />
                          Excel
                        </button>
                      </div>
                    </div>

                    <div className="px-4 pb-4 pt-3 border-0 border-t border-solid border-[--gr-2] flex flex-wrap items-center gap-2">
                      <div className="w-full sm:w-44">
                        <CustomSelect
                          value={
                            customDate
                              ? customDate
                              : { label: t("options.customDate") }
                          }
                          isSearchable={false}
                          options={presetOptions}
                          onChange={handleOptionChange}
                          className="w-full rounded-md bg-[--btn-txt] text-sm"
                          isDisabled={dataLoading}
                        />
                      </div>
                      <div className="w-full sm:w-auto sm:flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <DateTimePicker
                          value={startDate ? startDate : ""}
                          onChange={setStartDate}
                          placeholder={t("compare.startD")}
                          isDisabled={dataLoading}
                        />
                        <DateTimePicker
                          value={endDate ? endDate : ""}
                          onChange={setEndDate}
                          placeholder={t("compare.endD")}
                          isDisabled={dataLoading}
                        />
                      </div>
                      <button
                        type="button"
                        className="w-full sm:w-auto bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-sm font-medium px-4 py-2.5 text-white rounded-md disabled:opacity-50"
                        onClick={() => submitDateRange()}
                        disabled={dataLoading}
                      >
                        {t("reports.create")}
                      </button>
                      <div className="w-full lg:w-64 lg:ml-auto">
                        <CustomInput
                          type="text"
                          placeholder={t("nav.search")}
                          ariaLabel={t("nav.search")}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full p-2.5 rounded-md text-[--gr-9] text-sm border border-[--gr-3] border-solid"
                        />
                      </div>
                    </div>
                  </div>

                  {dataLoading && (
                    <div className="mb-3">
                      <div
                        className="progress-bar"
                        role="progressbar"
                        aria-label={t("reports.refreshing")}
                      />
                      <p className="mt-1 text-xs text-[--tx-3]">
                        {retryText || t("reports.refreshing")}
                      </p>
                    </div>
                  )}

                  {!dataLoading && dataError && stale && (
                    <div
                      className="mb-3 rounded-lg border border-solid border-amber-400 bg-amber-50 text-amber-900 px-4 py-2.5 text-sm flex flex-wrap items-center justify-between gap-2"
                      role="alert"
                    >
                      <span>
                        {t("reports.staleWarning")} {humanizeError(dataError, t)}
                      </span>
                      <button
                        type="button"
                        className="font-medium underline"
                        onClick={() => submitDateRange()}
                      >
                        {t("404.tryagain")}
                      </button>
                    </div>
                  )}

                  <div
                    className={
                      dataLoading ? "opacity-60 transition-opacity" : ""
                    }
                    aria-busy={dataLoading}
                  >
                    {filteredTables && !isTableEmpty ? (
                      filteredTables.map((table, index) =>
                        table.rows.length > 0 || searchQuery === "" ? (
                          <MyTable
                            key={`${table.name}-${index}`}
                            table={table}
                            showColumns={true}
                            showTitle={true}
                            showTickDetail={showTickDetail}
                          />
                        ) : null
                      )
                    ) : (
                      <ErrorDisplay error={t("errors.noResults")} />
                    )}
                  </div>
                </>
              ) : dataLoading || reportsLoading ? (
                <div aria-busy="true">
                  <div className="mb-3 rounded-xl border border-solid border-[--gr-3] p-4 animate-pulse">
                    <div className="h-3 bg-[--gr-2] w-1/4 rounded mb-2"></div>
                    <div className="h-6 bg-[--gr-2] w-2/3 rounded mb-2"></div>
                    <div className="h-3 bg-[--gr-2] w-1/3 rounded"></div>
                  </div>
                  {retryText && (
                    <p className="mb-2 text-sm text-amber-600" role="status">
                      {retryText}
                    </p>
                  )}
                  <TableSkeleton />
                  <TableSkeleton />
                </div>
              ) : dataError ? (
                <ErrorDisplay
                  error={dataError}
                  onRetry={() => submitDateRange()}
                />
              ) : reportsSuccess && reports.length === 0 ? (
                <ErrorDisplay error={t("errors.noResults")} />
              ) : null}
            </div>
          </div>
        </main>
      </div>
    </section>
  );
};

export default ReportDetails;
